// Mercapoli - Admin Categories with CRUD
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, LayoutGrid, Plus, Loader2, Edit, Trash2, 
  Save, X, Image as ImageIcon 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { categorias as categoriasApi } from '../../lib/api';
import { toast } from 'sonner';

const CLOUDINARY_UPLOAD_PRESET = 'mercapoli_unsigned';
const CLOUDINARY_CLOUD_NAME = 'mercapoli';

const AdminCategories = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    icono: '🍽️',
    imagen: '',
    orden: 0,
    activo: true
  });

  useEffect(() => {
    loadCategorias();
  }, []);

  const loadCategorias = async () => {
    try {
      const data = await categoriasApi.getAll();
      setCategorias(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar categorías');
    }
    setLoading(false);
  };

  const handleNew = () => {
    setSelectedCategoria(null);
    setFormData({
      nombre: '',
      descripcion: '',
      icono: '🍽️',
      imagen: '',
      orden: categorias.length,
      activo: true
    });
    setShowModal(true);
  };

  const handleEdit = (categoria) => {
    setSelectedCategoria(categoria);
    setFormData({
      nombre: categoria.nombre || '',
      descripcion: categoria.descripcion || '',
      icono: categoria.icono || '🍽️',
      imagen: categoria.imagen || '',
      orden: categoria.orden || 0,
      activo: categoria.activo !== false
    });
    setShowModal(true);
  };

  const handleDelete = (categoria) => {
    setSelectedCategoria(categoria);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedCategoria) return;
    
    try {
      await categoriasApi.delete(selectedCategoria.categoria_id);
      toast.success('Categoría eliminada');
      loadCategorias();
    } catch (error) {
      toast.error('Error al eliminar categoría');
    }
    setShowDeleteDialog(false);
    setSelectedCategoria(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formDataUpload.append('folder', 'mercapoli/categorias');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formDataUpload }
      );

      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setFormData(prev => ({ ...prev, imagen: data.secure_url }));
      toast.success('Imagen subida');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error al subir imagen');
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (selectedCategoria) {
        // Actualizar
        await categoriasApi.update(selectedCategoria.categoria_id, formData);
        toast.success('Categoría actualizada');
      } else {
        // Crear
        await categoriasApi.create(formData);
        toast.success('Categoría creada');
      }
      setShowModal(false);
      loadCategorias();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  const iconOptions = ['🍽️', '🍔', '🍕', '🌮', '🍣', '🥗', '☕', '🍰', '🍺', '🛒', '💊', '🏪', '🎁', '🌿'];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Categorías</h1>
            </div>
            <Button 
              onClick={handleNew}
              className="bg-brand-primary hover:bg-brand-secondary"
              data-testid="new-category-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Categoría
            </Button>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
          </div>
        ) : categorias.length === 0 ? (
          <div className="text-center py-12">
            <LayoutGrid className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No hay categorías</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={handleNew}
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear primera categoría
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categorias.map((cat) => (
              <Card key={cat.categoria_id} className="overflow-hidden group">
                <div className="aspect-video relative">
                  {cat.imagen ? (
                    <img 
                      src={cat.imagen} 
                      alt={cat.nombre}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center text-4xl">
                      {cat.icono || '🍽️'}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white font-bold">{cat.nombre}</p>
                    <p className="text-white/80 text-xs">{cat.icono}</p>
                  </div>
                  
                  {/* Overlay con acciones */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button 
                      size="sm" 
                      variant="secondary"
                      onClick={() => handleEdit(cat)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDelete(cat)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedCategoria ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
            <DialogDescription>
              {selectedCategoria 
                ? 'Modifica los datos de la categoría'
                : 'Ingresa los datos de la nueva categoría'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Ej: Restaurantes, Farmacias..."
                data-testid="category-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción breve de la categoría"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Icono</Label>
              <div className="flex flex-wrap gap-2">
                {iconOptions.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, icono: icon }))}
                    className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl transition-colors ${
                      formData.icono === icon 
                        ? 'border-brand-primary bg-brand-primary/10' 
                        : 'border-border hover:border-brand-primary/50'
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Imagen</Label>
              <div className="flex items-center gap-4">
                {formData.imagen ? (
                  <div className="relative w-24 h-16 rounded-lg overflow-hidden">
                    <img 
                      src={formData.imagen} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, imagen: '' }))}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="max-w-[200px]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG, WebP. Max 5MB
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="orden">Orden de visualización</Label>
              <Input
                id="orden"
                type="number"
                value={formData.orden}
                onChange={(e) => setFormData(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))}
                min={0}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving || uploading}
              className="bg-brand-primary hover:bg-brand-secondary"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {selectedCategoria ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Eliminación */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La categoría "{selectedCategoria?.nombre}" 
              será eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCategories;
